import React from 'react'

/**
 * AbwaabPlus Button — the primary action element. One primary action per screen.
 * All CTAs (incl. purchase) use the brand-blue `primary` variant.
 *
 * Portal-native recreation of the design-system Button
 * (abwaabplus-design-system/project/components/core/Button.jsx), reading the
 * CSS-variable token layer so it recolors with the active theme.
 */
export type AbwButtonVariant =
	| 'primary'
	| 'success'
	| 'destructive'
	| 'outline'
	| 'destructive-outline'
	| 'secondary'
	| 'ghost'
	| 'link'

export type AbwButtonSize = 'xs' | 'sm' | 'default' | 'lg'

type SizeSpec = { height: number; padding: string; fontSize: number }

const SIZES: Record<AbwButtonSize, SizeSpec> = {
	xs: { height: 24, padding: '0 8px', fontSize: 12 },
	sm: { height: 32, padding: '0 12px', fontSize: 14 },
	default: { height: 36, padding: '0 16px', fontSize: 14 },
	lg: { height: 40, padding: '0 24px', fontSize: 14 },
}

const VARIANTS: Record<AbwButtonVariant, React.CSSProperties> = {
	primary: { background: 'var(--blue)', color: 'var(--text-on-brand)', border: '1px solid transparent' },
	success: { background: 'var(--success)', color: 'var(--text-on-brand)', border: '1px solid transparent' },
	destructive: { background: 'var(--error)', color: 'var(--text-on-brand)', border: '1px solid transparent' },
	outline: { background: 'var(--surface-card)', color: 'var(--text-primary)', border: '1px solid var(--border)' },
	'destructive-outline': { background: 'var(--surface-card)', color: 'var(--error)', border: '1px solid var(--error)' },
	secondary: { background: 'var(--surface-page)', color: 'var(--blue)', border: '1px solid transparent' },
	ghost: { background: 'transparent', color: 'var(--text-primary)', border: '1px solid transparent' },
	link: { background: 'transparent', color: 'var(--blue)', border: 'none', textDecoration: 'underline', textUnderlineOffset: 4 },
}

export interface AbwButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: AbwButtonVariant
	size?: AbwButtonSize
	/** Full-width 48px CTA (clears the 44px touch target). */
	fullWidth?: boolean
	/** Alias for fullWidth. */
	block?: boolean
	leadingIcon?: React.ReactNode
	trailingIcon?: React.ReactNode
}

export const Button = React.forwardRef<HTMLButtonElement, AbwButtonProps>(function Button(
	{ variant = 'primary', size = 'default', fullWidth = false, block = false, disabled = false, leadingIcon, trailingIcon, children, style, onMouseEnter, onMouseLeave, ...rest },
	ref,
) {
	const s = SIZES[size] || SIZES.default
	const cta = fullWidth || block
	const v = VARIANTS[variant]

	return (
		<button
			ref={ref}
			disabled={disabled}
			style={{
				font: `var(--fw-${variant === 'primary' || cta ? 'bold' : 'medium'}) ${s.fontSize}px var(--font-sans)`,
				cursor: disabled ? 'not-allowed' : 'pointer',
				borderRadius: 'var(--r-md)',
				height: cta ? 48 : s.height,
				padding: cta ? '0 16px' : s.padding,
				width: cta ? '100%' : 'auto',
				display: 'inline-flex',
				alignItems: 'center',
				justifyContent: 'center',
				gap: 8,
				whiteSpace: 'nowrap',
				opacity: disabled ? 0.5 : 1,
				transition: 'background var(--dur-fast) var(--ease), box-shadow var(--dur-fast) var(--ease), filter var(--dur-fast) var(--ease)',
				...v,
				...style,
			}}
			onMouseEnter={e => {
				if (!disabled) {
					if (variant === 'primary') e.currentTarget.style.background = 'var(--blue-hover)'
					else if (variant === 'success' || variant === 'destructive') e.currentTarget.style.filter = 'brightness(0.95)'
					else if (variant === 'ghost' || variant === 'outline') e.currentTarget.style.background = 'var(--surface-page)'
				}
				onMouseEnter?.(e)
			}}
			onMouseLeave={e => {
				e.currentTarget.style.filter = 'none'
				e.currentTarget.style.background = (v.background as string) ?? 'transparent'
				onMouseLeave?.(e)
			}}
			{...rest}
		>
			{leadingIcon}
			{children}
			{trailingIcon}
		</button>
	)
})
